import { createSelector } from 'reselect';

import { reportingCurrencyCodeSelector, companyCodesSelector } from '../../../shared/store/selectors';
import { groupTypeColumns, groupTypeValues, OPTION_ALL } from '../constants';
import { FUNCTIONAL_CURRENCY } from '@common/constants/sharedConstants';
import getColumnDefs from '../getColumnDefs';
import { displayZeroSelector, hideDecimalsSelector, negativeMinusSelector } from '../../../userData/store/selectors';
import { BOOK_TYPES } from '../../shared/constants';

export const isExportingReportForAllPillsSelector = ({ reports }) => (
  reports.taxExpense.isExportingReportForAllPills
);

export const REPORT_BOOK_TYPES_ALL_OPTION = '__ALL__';

export const taxExpenseRowsSelector = ({ reports }) => reports.taxExpense.reportData.rows;

export const reportBookTypeSelector = ({ reports }) => reports.taxExpense.reportBookType;
export const selectedReportBookTypesSelector = ({ reports }) => reports.taxExpense.selectedReportBookTypes;

const reportBookTypeOptionsMap = {
  [BOOK_TYPES.US_SUMMARY]: { label: 'US Summary', value: BOOK_TYPES.US_SUMMARY },
  [BOOK_TYPES.US_FED]: { label: 'US Federal', value: BOOK_TYPES.US_FED },
  [BOOK_TYPES.US_STATE]: { label: 'US State', value: BOOK_TYPES.US_STATE },
  [BOOK_TYPES.TED_NON_US_GAAP]: { label: 'GAAP Non-US', value: BOOK_TYPES.TED_NON_US_GAAP },
  [BOOK_TYPES.TED_NON_US_STAT]: { label: 'STAT Non-US', value: BOOK_TYPES.TED_NON_US_STAT },
};

export const reportBookTypesOptionsSelector = createSelector(
  (state, props) => props.useHistorical,
  useHistorical => [
    { label: 'ALL', value: REPORT_BOOK_TYPES_ALL_OPTION },
    ...Object.values(reportBookTypeOptionsMap)
      .filter(option => !useHistorical || option.value !== BOOK_TYPES.US_SUMMARY),
  ]
);

export const selectedReportBookTypeOptionsSelector = createSelector(
  selectedReportBookTypesSelector,
  reportBookTypesOptionsSelector,
  (selectedReportBookTypes, reportBookTypeOptions) => reportBookTypeOptions
    .filter(({ value }) => value !== REPORT_BOOK_TYPES_ALL_OPTION)
    .filter(({ value }) => selectedReportBookTypes.includes(value))
);

export const groupTypeSelector = ({ reports }) =>
  reports.taxExpense.groupType === groupTypeValues.CUSTOM_GROUP
    ? groupTypeValues.COMPANY_CODES
    : reports.taxExpense.groupType;

export const groupFieldSelector = createSelector(
  groupTypeSelector,
  groupType => groupTypeColumns[groupType] || ''
);

export const drillDetailSelector = ({ reports }) => reports.taxExpense.drillDetail;

export const isDrillDetailLoadingSelector = ({ reports }) => reports.taxExpense.isDrillDetailLoading;

export const selectedEntryForDrillDetailSelector = ({ reports }) => reports.taxExpense.selectedEntry;

export const companyCodesOptionsSelector = createSelector(
  companyCodesSelector,
  groupTypeSelector,
  (companyCodes, groupType) => {
    if (groupType !== groupTypeValues.COMPANY_CODES) {
      return null;
    }

    return [
      OPTION_ALL,
      ...companyCodes.map(({ cc }) => ({
        label: cc,
        value: cc,
      })),
    ];
  }
);

export const companyCodeToFilterSelector = ({ reports }) => reports.taxExpense.companyCodeToFilter;

export const customGroupOptionsSelector = ({ reports }) => reports.taxExpense.customGroupOptions;
export const customGroupSelector = ({ reports }) => reports.taxExpense.customGroup;

export const countryTaxTypesOptionsSelector = createSelector(
  ({ reports }) => reports.taxExpense.countryTaxTypesOptions,
  countryTaxTypes => [OPTION_ALL, ...countryTaxTypes.map(({ countryTaxType, description }) => ({
    label: description,
    value: countryTaxType,
  }))]
);

export const countryTaxTypeToFilterSelector = ({ reports }) => reports.taxExpense.countryTaxType;

export const selectedCountryToTaxTypeDescriptionSelector = createSelector(
  countryTaxTypesOptionsSelector,
  countryTaxTypeToFilterSelector,
  (countryTaxTypes, selectedCountryTaxTypeValue) => {
    const selectedCountryTaxType = countryTaxTypes.find(({ value }) => value ===selectedCountryTaxTypeValue);
    return selectedCountryTaxType && selectedCountryTaxType.label;
  }
);

export const detailAggregationSelector = ({ reports }) => reports.taxExpense.detailAggregationType;

export const columnDefsSelector = createSelector(
  state => state.reports.taxExpense.reportBookType,
  state => state.reports.taxExpense.groupType,
  state => state.reports.taxExpense.companyCodeToFilter,
  reportingCurrencyCodeSelector,
  (state, props) => props.useHistorical,
  displayZeroSelector,
  hideDecimalsSelector,
  negativeMinusSelector,
  (
    reportBookType,
    groupType,
    companyCodeToFilter,
    reportingCurrencyCode,
    useHistorical,
    shouldDisplayZero,
    hideDecimals,
    negativeMinus
  ) => getColumnDefs(
    reportBookType,
    groupType,
    companyCodeToFilter,
    {
      useHistorical,
      hasSourceCurrency: reportingCurrencyCode === FUNCTIONAL_CURRENCY,
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
    }
  )
);
